import { useMyMutation, MutationInfos, client } from '../helpers';
import {
    EditRideInput,
    DeleteRideInput,
    CreateRideInput,
    CreateRideRecurrentInput,
    UpdateRideRecurrentInput,
    DeleteRideRecurrentInput,
    SOSRideInput,
    PauseRecurrentInput,
    UnPauseRecurrentInput,
    UpdatePauseRecurrentInput,
    CancelRideInput,
} from '../../apiTypes';
import { typeRides } from './types';
import { ToastWithError, ToastWithIcon } from '../../../navigation/routes/toastProvider';
import { stringsRes } from '../../../resources/strings';
import { paths } from '../../../navigation/routes/paths';
import { isDuplicateRideError } from './createRideError';

const noToastPages = [
    paths.homeService,
    paths.homeScheduleFlightAdditional,
    paths.homeConfirm,
    paths.homeConfirmDetail,
    paths.homeDriverSearch,
];

// Mutation function
function updateRide(variables: EditRideInput): MutationInfos {
    return {
        method: 'put',
        path: `/rides`,
        type: typeRides.UPDATE_RIDE,
        params: {
            ...variables,
        },
    };
}

function cancelRide(variables: CancelRideInput): MutationInfos {
    return {
        method: 'put',
        path: `/rides/${variables?.id}/cancel`,
        type: typeRides.CANCEL_RIDE,
        params: {
            ...variables,
        },
    };
}

function deleteRide(variables: DeleteRideInput): MutationInfos {
    return {
        method: 'delete',
        path: `/rides/${variables?.id}`,
        type: typeRides.DELETE_RIDE,
        params: {
            ...variables,
        },
    };
}

function createRide(variables: CreateRideInput): MutationInfos {
    return {
        method: 'post',
        path: `/rides`,
        type: typeRides.CREATE_RIDE,
        params: {
            ...variables,
        },
    };
}

function sosRide(variables: SOSRideInput): MutationInfos {
    return {
        method: 'put',
        path: `rides/${variables.id}/trigger-emergency`,
        type: typeRides.SOS_RIDE,
        params: {
            ...variables,
        },
    };
}

function sosRideCancel(variables: SOSRideInput): MutationInfos {
    return {
        method: 'put',
        path: `rides/${variables.id}/cancel-emergency`,
        type: typeRides.SOS_RIDE_CANCEL,
        params: {
            ...variables,
        },
    };
}

//= ==========================================//
//= ============== Visible Use ===============//
//= ==========================================//

export function useUpdateRide(noToast = false) {
    return useMyMutation(updateRide, {
        onSuccess: (_, variables) => {
            // Update some cache with this key ref
            client.invalidateQueries('historySoon');
            client.invalidateQueries(`ride${variables.id}`);
            client.resetQueries('quotes');
            // we can update the local with returned data
            // queryClient.setQueryData(['Rides', { id }], data);
            // we could do optimistic ui too
            if (
                !noToast &&
                window.location.pathname !== paths.home &&
                !window.location.pathname.includes(paths.homeTrackingMobile.replace(':id', ''))
            ) {
                ToastWithIcon('info', stringsRes.common.edited);
            }
        },
    });
}

export function useCancelRide() {
    return useMyMutation(cancelRide, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
        onError: (error) => {
            ToastWithError(error, stringsRes.booking.toast.cancelError);
        },
    });
}

export function useSOSRide() {
    return useMyMutation(sosRide, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
    });
}

export function useCancelSOSRide() {
    return useMyMutation(sosRideCancel, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
    });
}

export function useDeleteRide() {
    return useMyMutation(deleteRide, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
    });
}

export function useCreateRide() {
    return useMyMutation(createRide, {
        retry: 0,
        onSuccess: () => {
            client.invalidateQueries('historySoon');
            client.resetQueries('quotes');
            if (!noToastPages.includes(window.location.pathname)) {
                ToastWithIcon('success', stringsRes.booking.toast.created);
            }
        },
        onError: (error) => {
            if (isDuplicateRideError(error)) {
                ToastWithIcon('error', stringsRes.booking.status.duplicate_ride);
            }
        },
    });
}

// =================================//
// =========== RECURRRINGS =========//
// =================================//

function createRideRecurrent(variables: CreateRideRecurrentInput): MutationInfos {
    return {
        method: 'post',
        path: `/recurrings`,
        type: typeRides.CREATE_RIDE_RECURRENT,
        params: {
            ...variables,
        },
    };
}

function updateRideRecurrent(variables: UpdateRideRecurrentInput): MutationInfos {
    const { id, ...rest } = variables;
    return {
        method: 'put',
        path: `/recurrings/${id}`,
        type: typeRides.UPDATE_RIDE_RECURRENT,
        params: {
            ...rest,
        },
    };
}
function deleteRideRecurrent(variables: DeleteRideRecurrentInput): MutationInfos {
    return {
        method: 'delete',
        path: `/recurrings/${variables.id}`,
        type: typeRides.DELETE_RIDE_RECURRENT,
        params: {},
    };
}

function pauseRideRecurrent(variables: PauseRecurrentInput): MutationInfos {
    return {
        method: 'post',
        path: `/recurring/breaks`,
        type: typeRides.PAUSE_RIDE_RECURRING,
        params: {
            ...variables,
        },
    };
}

function updatePauseRideRecurrent(variables: UpdatePauseRecurrentInput): MutationInfos {
    return {
        method: 'put',
        path: `/recurring/breaks/${variables.break_id}`,
        type: typeRides.UPDATE_PAUSE_RIDE_RECURRING,
        params: {
            ...variables,
        },
    };
}

function unPauseRideRecurrent(variables: UnPauseRecurrentInput): MutationInfos {
    return {
        method: 'delete',
        path: `/recurring/breaks/${variables.break_id}`,
        type: typeRides.UN_PAUSE_RIDE_RECURRING,
        params: {},
    };
}

export function useCreateRideRecurrent() {
    return useMyMutation(createRideRecurrent, {
        retry: 0,
        onSuccess: () => {
            client.invalidateQueries('historySoon');
            client.resetQueries('quotes');
            if (!noToastPages.includes(window.location.pathname)) {
                ToastWithIcon('success', stringsRes.booking.toast.createdRecurrence);
            }
        },
        onError: () => {},
    });
}

export function useUpdateRideRecurrent() {
    return useMyMutation(updateRideRecurrent, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
            client.resetQueries('quotes');
        },
    });
}

export function useDeleteRideRecurrent() {
    return useMyMutation(deleteRideRecurrent, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
        onError: (error) => {
            ToastWithError(error, stringsRes.booking.toast.cancelRecurrentError);
        },
    });
}

export function usePauseRideRecurrent() {
    return useMyMutation(pauseRideRecurrent, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
    });
}

export function useUpdatePauseRideRecurrent() {
    return useMyMutation(updatePauseRideRecurrent, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
    });
}

export function useUnPauseRideRecurrent() {
    return useMyMutation(unPauseRideRecurrent, {
        onSuccess: () => {
            client.invalidateQueries('historySoon');
        },
    });
}
